import React, { createContext, useState, useEffect, useCallback } from 'react';
import { 
  getPosts, 
  getUserFeed, 
  createPost, 
  likePost, 
  unlikePost,
  getPostById,
  getUserPosts,
  getCommunityPosts
} from '../api/posts';
import useAuth from '../hooks/useAuth';
import useWebSocket from '../hooks/useWebSocket';

export const PostContext = createContext(null);

export const PostProvider = ({ children }) => {
  const { user, isAuth } = useAuth();
  const { addEventListener } = useWebSocket();
  
  const [posts, setPosts] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [userPosts, setUserPosts] = useState([]);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [singlePost, setSinglePost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const fetchPosts = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPosts(options);
      
      if (response && response.data && response.data.data && Array.isArray(response.data.data.posts)) {
        setPosts(response.data.data.posts);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        return response.data;
      } else if (response && response.data && Array.isArray(response.data.posts)) {
        setPosts(response.data.posts);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        return response.data;
      } else {
        console.error('Invalid response format from getPosts:', response);
        setPosts([]);
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError(error.message || 'Failed to load posts');
      setPosts([]);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeed = useCallback(
    async (options = {}) => {
      if (!isAuth) {
        throw new Error('Authentication required');
      }
      
      try {
        setLoading(true);
        setError(null);
        const response = await getUserFeed(options);
        
        if (response && response.data && response.data.data && Array.isArray(response.data.data.posts)) {
          setFeedPosts(response.data.data.posts);
          if (response.data.pagination) {
            setPagination(response.data.pagination);
          }
          return response.data;
        } else if (response && response.data && Array.isArray(response.data.posts)) {
          setFeedPosts(response.data.posts);
          if (response.data.pagination) {
            setPagination(response.data.pagination);
          }
          return response.data;
        } else {
          console.error('Invalid response format from getUserFeed:', response);
          setFeedPosts([]);
          throw new Error('Invalid response format from server');
        }
      } catch (error) {
        console.error('Error fetching feed:', error);
        setError(error.message || 'Failed to load feed');
        setFeedPosts([]);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [isAuth]
  );

  const fetchPostById = useCallback(async (postId) => {
    if (!postId) {
      console.error('No post ID provided');
      setError('No post ID provided');
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await getPostById(postId);
      
      let post = null;

      if (response?.data?.data?.post) {
        post = response.data.data.post;
      } else if (response?.data?.post) {
        post = response.data.post;
      } else {
        console.error('Invalid response format from getPostById:', response);
        throw new Error('Invalid response format from server');
      }
      
      setSinglePost(post);
      return post;
    } catch (error) {
      console.error('Error fetching post:', error);
      setError(error.message || 'Failed to load post');
      setSinglePost(null);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserPosts = useCallback(async (userId, options = {}) => {
    if (!userId) {
      console.error('No user ID provided');
      setError('No user ID provided');
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await getUserPosts(userId, options);
      
      if (response && response.data && response.data.data && Array.isArray(response.data.data.posts)) {
        setUserPosts(response.data.data.posts);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        return response.data;
      } else if (response && response.data && Array.isArray(response.data.posts)) {
        setUserPosts(response.data.posts);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        return response.data;
      } else {
        console.error('Invalid response format from getUserPosts:', response);
        setUserPosts([]);
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error fetching user posts:', error);
      setError(error.message || 'Failed to load user posts');
      setUserPosts([]);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCommunityPosts = useCallback(async (communityId, options = {}) => {
    if (!communityId) {
      console.error('No community ID provided');
      setError('No community ID provided');
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await getCommunityPosts(communityId, options);
      
      if (response && response.data && response.data.data && Array.isArray(response.data.data.posts)) {
        setCommunityPosts(response.data.data.posts);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        return response.data;
      } else if (response && response.data && Array.isArray(response.data.posts)) {
        setCommunityPosts(response.data.posts);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        return response.data;
      } else {
        console.error('Invalid response format from getCommunityPosts:', response);
        setCommunityPosts([]);
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error fetching community posts:', error);
      setError(error.message || 'Failed to load community posts');
      setCommunityPosts([]);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const addPost = useCallback(
    async (postData, onUploadProgress) => {
      if (!isAuth) {
        throw new Error('Authentication required');
      }
      
      try {
        setLoading(true);
        setError(null);
        const response = await createPost(postData, onUploadProgress);

        let newPost = null;
        
        if (response?.data?.data?.post) {
          newPost = response.data.data.post;
        } else if (response?.data?.post) {
          newPost = response.data.post;
        } else {
          console.error('Invalid response format from createPost:', response);
          throw new Error('Invalid response format from server');
        }
        
        if (newPost && newPost._id) {
          // Update feed if we're in the feed view
          setFeedPosts((prevPosts) => [newPost, ...(prevPosts || [])]);
          
          // Update all posts if we're in the main view
          setPosts((prevPosts) => [newPost, ...(prevPosts || [])]);
          
          // Update user posts if it's the current user's post
          if (user && newPost.user && user._id === newPost.user._id) {
            setUserPosts((prevPosts) => [newPost, ...(prevPosts || [])]);
          }
          
          // Update community posts if it's in a community
          if (newPost.community) {
            setCommunityPosts((prevPosts) => [newPost, ...(prevPosts || [])]);
          }
          
          return newPost;
        } else {
          throw new Error('Invalid post data in server response');
        }
      } catch (error) {
        console.error('Error creating post:', error);
        setError(error.message || 'Failed to create post');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [isAuth, user]
  );

  const handleLikePost = useCallback(
    async (postId) => {
      if (!isAuth || !user) {
        throw new Error('Authentication required');
      }
      
      if (!postId) {
        console.error('No post ID provided');
        throw new Error('No post ID provided');
      }
      
      try {
        setError(null);
        const response = await likePost(postId);

        const updatePostInArray = (posts) => {
          if (!Array.isArray(posts)) return [];
          
          return posts.map((post) => {
            if (!post || post._id !== postId) return post;
            
            return {
              ...post,
              likes: [...(post.likes || []), user._id],
              likesCount: (post.likesCount || 0) + 1,
            };
          });
        };
        
        setPosts(updatePostInArray(posts));
        setFeedPosts(updatePostInArray(feedPosts));
        setUserPosts(updatePostInArray(userPosts));
        setCommunityPosts(updatePostInArray(communityPosts));

        if (singlePost && singlePost._id === postId) {
          setSinglePost({
            ...singlePost,
            likes: [...(singlePost.likes || []), user._id],
            likesCount: (singlePost.likesCount || 0) + 1,
          });
        }
        
        return response.data;
      } catch (error) {
        console.error('Error liking post:', error);
        setError(error.message || 'Failed to like post');
        throw error;
      }
    },
    [isAuth, user, posts, feedPosts, userPosts, communityPosts, singlePost]
  );

  const handleUnlikePost = useCallback(
    async (postId) => {
      if (!isAuth || !user) {
        throw new Error('Authentication required');
      }
      
      if (!postId) {
        console.error('No post ID provided');
        throw new Error('No post ID provided');
      }
      
      try {
        setError(null);
        const response = await unlikePost(postId);

        const updatePostInArray = (posts) => {
          if (!Array.isArray(posts)) return [];
          
          return posts.map((post) => {
            if (!post || post._id !== postId) return post;
            
            return {
              ...post,
              likes: (post.likes || []).filter((id) => id !== user._id),
              likesCount: Math.max((post.likesCount || 0) - 1, 0),
            };
          });
        };
        
        setPosts(updatePostInArray(posts));
        setFeedPosts(updatePostInArray(feedPosts));
        setUserPosts(updatePostInArray(userPosts));
        setCommunityPosts(updatePostInArray(communityPosts));

        if (singlePost && singlePost._id === postId) {
          setSinglePost({
            ...singlePost,
            likes: (singlePost.likes || []).filter((id) => id !== user._id),
            likesCount: Math.max((singlePost.likesCount || 0) - 1, 0),
          });
        }
        
        return response.data;
      } catch (error) {
        console.error('Error unliking post:', error);
        setError(error.message || 'Failed to unlike post');
        throw error;
      }
    },
    [isAuth, user, posts, feedPosts, userPosts, communityPosts, singlePost]
  );

  useEffect(() => {
    if (!isAuth || !user) return;

    const cleanup = addEventListener('new_post', (data) => {
      if (data.post) {
        if (
          user.following && Array.isArray(user.following) &&
          (user.following.includes(data.post.user._id) ||
          data.post.user._id === user._id)
        ) {
          setFeedPosts((prevPosts) => [data.post, ...(prevPosts || [])]);
        }

        if (
          data.post.community &&
          communityPosts.length > 0 &&
          communityPosts[0].community &&
          communityPosts[0].community._id === data.post.community._id
        ) {
          setCommunityPosts((prevPosts) => [data.post, ...(prevPosts || [])]);
        }
      }
    });
    
    return cleanup;
  }, [isAuth, user, addEventListener, communityPosts]);

  const postContextValue = {
    posts,
    feedPosts,
    userPosts,
    communityPosts,
    singlePost,
    loading,
    error,
    pagination,
    fetchPosts,
    fetchFeed,
    fetchPostById,
    fetchUserPosts,
    fetchCommunityPosts,
    createPost: addPost,
    likePost: handleLikePost,
    unlikePost: handleUnlikePost,
  };
  
  return (
    <PostContext.Provider value={postContextValue}>
      {children}
    </PostContext.Provider>
  );
};

export default PostProvider;